import { motion } from "framer-motion";
import { Building2, Users, Globe2, Award, TrendingUp, Shield, Zap, CheckCircle2 } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

export function About() {
  const features = [
    {
      icon: Award,
      title: "ISCC Certified",
      description: "Globally recognized sustainability certification for renewable fuel feedstock",
      stat: "100%",
      statLabel: "Compliant",
    },
    {
      icon: Shield,
      title: "Digital Traceability",
      description: "Complete origin-to-delivery tracking with IoT-enabled B-Trace platform",
      stat: "24/7",
      statLabel: "Monitored",
    },
    {
      icon: Zap,
      title: "Circular Economy",
      description: "Converting post-consumer waste into valuable renewable energy resources",
      stat: "Zero",
      statLabel: "Waste",
    },
    {
      icon: TrendingUp,
      title: "Scalable Solutions",
      description: "Enterprise-grade infrastructure for biofuel manufacturers worldwide",
      stat: "2G&3G",
      statLabel: "Feedstock",
    },
  ];

  const journey = [
    {
      step: "01",
      title: "Collection",
      description: "Gathering used cooking oil from FBOs across India",
      icon: Building2,
    },
    {
      step: "02",
      title: "Processing",
      description: "Filtering and consolidating UCO to fuel-grade standards",
      icon: Zap,
    },
    {
      step: "03",
      title: "Certification",
      description: "ISCC documentation with complete batch traceability",
      icon: Award,
    },
    {
      step: "04",
      title: "Distribution",
      description: "Delivering certified feedstock to biofuel manufacturers",
      icon: Globe2,
    },
  ];

  return (
    <section id="about" className="relative py-20 lg:py-32 bg-white overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#7CB342]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#1976D2]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7CB342]/10 to-[#1976D2]/10 border-2 border-[#7CB342]/30 rounded-full px-5 py-2.5 mb-6"
          >
            <div className="w-2 h-2 bg-[#7CB342] rounded-full animate-pulse" />
            <span className="text-sm font-bold text-gray-900">About KRB Clean Energy</span>
          </motion.div>

          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Pioneering the{" "}
            <span className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">
              Clean Energy Revolution
            </span>
          </h2>

          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            <span className="font-bold text-gray-900">KRB Clean Energy Private Limited</span> is transforming India's waste management landscape by converting Used Cooking Oil (UCO) into certified renewable fuel feedstock for a sustainable tomorrow.
          </p>
        </motion.div>

        {/* Image + Stats Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mb-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1610547939489-73202bc6afda?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb29raW5nJTIwb2lsJTIwZ29sZGVuJTIwbGlxdWlkfGVufDF8fHx8MTc2NTA5MDI5Nnww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Clean Energy Process"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#1976D2]/20 rounded-full blur-2xl" />
          </motion.div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid sm:grid-cols-2 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group bg-white border-2 border-gray-200 hover:border-[#7CB342] rounded-2xl p-6 transition-all hover:shadow-xl"
              >
                <div className="bg-gradient-to-br from-[#7CB342] to-[#1976D2] w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-2xl font-black text-gray-900 mb-1">{feature.stat}</div>
                <div className="text-xs font-bold text-gray-600 mb-3">{feature.statLabel}</div>
                <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Journey Process */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">
              Our <span className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">Journey</span>
            </h3>
            <p className="text-lg text-gray-600">
              From waste collection to certified clean energy
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {journey.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-6 h-full hover:border-[#7CB342] transition-all group">
                  {/* Step Number */}
                  <div className="text-6xl font-black text-gray-100 mb-4 group-hover:text-[#7CB342]/20 transition-colors">
                    {item.step}
                  </div>
                  
                  {/* Icon */}
                  <div className="bg-gradient-to-br from-[#7CB342] to-[#1976D2] w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Content */}
                  <h4 className="text-xl font-black text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>

                {/* Connector Arrow */}
                {index < journey.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-[#7CB342] to-[#1976D2]">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#1976D2] rounded-full" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Impact Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-3xl p-12 lg:p-16 text-center overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="1" fill="white"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#dots)" />
              </svg>
            </div>

            <div className="relative z-10">
              <h3 className="text-3xl lg:text-4xl font-black text-white mb-6">
                Creating a Sustainable Future Together
              </h3>
              <p className="text-lg text-white/90 max-w-3xl mx-auto mb-10">
                Join us in transforming waste into power. Every litre of UCO we collect contributes to reducing carbon emissions and building a cleaner, greener India.
              </p>

              <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
                {[
                  { icon: Users, value: "1000+", label: "Partner FBOs" },
                  { icon: Globe2, value: "Pan-India", label: "Coverage" },
                  { icon: Award, value: "ISCC", label: "Certified" },
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="bg-white/20 backdrop-blur-sm w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-3xl font-black text-white mb-1">{item.value}</div>
                    <div className="text-white/80 font-semibold">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}